import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
  PieChart, Pie, Cell,
} from 'recharts';
import {
  Send, ArrowDownLeft, CheckCheck, MessageSquare, Smartphone, Sparkles, Trophy,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/misc';
import { FreeQuotaGauge } from '@/components/FreeQuotaGauge';
import { OfficialChecklist } from '@/components/OfficialChecklist';
import { OfficialInsights } from '@/components/OfficialInsights';
import { useProjects } from '@/components/ProjectSwitcher';
import { getProject } from '@/lib/api';
import { isOfficial } from '@/lib/projects';
import { apiFetch, makeClient } from '@/lib/api';
import { formatMoney } from '@/lib/money';
import { formatPhone } from '@/lib/phone';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';

interface DayPoint { date: string; sent: number; received: number }
interface NumberUsage { instance_id: string; phone: string; total: number }
interface Usage {
  total?: number; sent?: number; received?: number; delivered?: number; read?: number;
  failed?: number; delivery_rate?: number; by_type?: Record<string, number>;
  by_number?: NumberUsage[]; series?: DayPoint[];
}
interface Instance { id: string; phone: string; nickname?: string; status: string }
interface PlanEff { plan: string; plan_monthly_cents: number; currency: string }

const PIE_COLORS = ['var(--primary)', '#f59e0b', '#10b981', '#6366f1', '#ef4444', '#06b6d4', '#a855f7', '#84cc16'];
const PERIODS = [7, 30, 90] as const;

/** Rótulo traduzido de status, com fallback para o valor cru se faltar chave. */
export function useStatusLabel() {
  const { t } = useI18n();
  return (s: string) => {
    const v = t(`status.${s}`);
    return v.includes('.') ? s : v;
  };
}

const DAY_MS = 86_400_000;
const iso = (d: Date) => d.toISOString();

export function Dashboard() {
  const { locale, t } = useI18n();
  const statusLabel = useStatusLabel();
  const [days, setDays] = useState<(typeof PERIODS)[number]>(7);
  const { data: projects = [] } = useProjects();
  const official = isOfficial(projects.find((p) => p.id === getProject()) ?? projects.find((p) => p.is_default));

  const range = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - days * DAY_MS);
    return { from: iso(from), to: iso(to), fromMs: from.getTime(), toMs: to.getTime() };
  }, [days]);

  const usage = useQuery({
    queryKey: ['usage', days],
    queryFn: async (): Promise<Usage> =>
      (await apiFetch('GET', `/usage?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`)).data as Usage ?? {},
  });
  const instances = useQuery({
    queryKey: ['instances'],
    queryFn: async (): Promise<Instance[]> => ((await makeClient().GET('/instances')).data as { data?: Instance[] })?.data ?? [],
  });
  const ent = useQuery({
    queryKey: ['entitlements'],
    retry: false,
    queryFn: async (): Promise<PlanEff | null> => {
      const r = await apiFetch('GET', '/me/entitlements');
      return r.ok ? (r.data as PlanEff) : null;
    },
  });

  const u = usage.data ?? {};
  const insts = useMemo(() => instances.data ?? [], [instances.data]);
  const connected = insts.filter((i) => i.status === 'connected' || i.status === 'warming').length;
  const statusOf = useMemo(() => Object.fromEntries(insts.map((i) => [i.id, i])), [insts]);

  // Série diária preenchida (dias sem mensagem viram 0) para uma curva contínua.
  const series = useMemo(() => {
    const map = new Map((u.series ?? []).map((d) => [d.date, d]));
    const out: { label: string; sent: number; received: number }[] = [];
    const start = new Date(range.fromMs); start.setHours(0, 0, 0, 0);
    for (let ts = start.getTime(); ts <= range.toMs; ts += DAY_MS) {
      const d = new Date(ts);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const hit = map.get(key);
      out.push({
        label: d.toLocaleDateString(locale, { day: '2-digit', month: 'short' }),
        sent: hit?.sent ?? 0,
        received: hit?.received ?? 0,
      });
    }
    return out;
  }, [u.series, range.fromMs, range.toMs, locale]);

  const byType = useMemo(
    () => Object.entries(u.by_type ?? {}).map(([type, n]) => ({ type, n })).sort((a, b) => b.n - a.n),
    [u.by_type],
  );
  const ranking = (u.by_number ?? []).slice(0, 6);
  const maxRank = ranking[0]?.total ?? 1;

  const rate = (u.delivery_rate ?? 0) * 100;
  const kpis = [
    { key: 'total', label: t('dashboard.total'), value: (u.total ?? 0).toLocaleString(locale), icon: MessageSquare, tone: 'text-primary' },
    { key: 'sent', label: t('dashboard.sent'), value: (u.sent ?? 0).toLocaleString(locale), icon: Send, tone: 'text-sky-500' },
    { key: 'received', label: t('dashboard.received'), value: (u.received ?? 0).toLocaleString(locale), icon: ArrowDownLeft, tone: 'text-emerald-500' },
    { key: 'rate', label: t('dashboard.deliveryRate'), value: `${rate.toFixed(1)}%`, icon: CheckCheck, tone: 'text-amber-500' },
  ];

  const plan = ent.data?.plan ?? 'free';
  const planLabel = plan === 'pro' ? 'Pro' : plan === 'exempt' ? 'Cortesia' : 'Free';

  return (
    <div className="flex flex-col gap-6">
      {official && <OfficialChecklist />}
      {/* Período */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
        <div className="inline-flex rounded-lg border bg-card p-0.5">
          {PERIODS.map((p) => (
            <button
              key={p}
              onClick={() => setDays(p)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                days === p ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t('dashboard.lastDays', { n: p })}
            </button>
          ))}
        </div>
      </div>

      {/* WhatsApp oficial: custo com a Meta e indicadores do trilho */}
      {official && <OfficialInsights days={days} />}

      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 *:min-w-0">
        {kpis.map((k) => (
          <Card key={k.key}>
            <CardHeader className="flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{k.label}</CardTitle>
              <k.icon className={cn('size-4', k.tone)} />
            </CardHeader>
            <CardContent>
              {usage.isLoading ? <Skeleton className="h-8 w-20" /> : <p className="text-3xl font-bold">{k.value}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tendência + Plano */}
      <div className="grid gap-6 lg:grid-cols-3 *:min-w-0">
        <Card className="lg:col-span-2">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>{t('dashboard.trend')}</CardTitle>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><span className="size-2.5 rounded-full bg-sky-500" />{t('dashboard.sent')}</span>
              <span className="flex items-center gap-1.5"><span className="size-2.5 rounded-full bg-emerald-500" />{t('dashboard.received')}</span>
            </div>
          </CardHeader>
          <CardContent>
            {usage.isLoading ? (
              <Skeleton className="h-70 w-full" />
            ) : (u.total ?? 0) === 0 ? (
              <p className="py-20 text-center text-sm text-muted-foreground">{t('dashboard.noUsage')}</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={series} margin={{ left: -20, right: 8, top: 8 }}>
                  <defs>
                    <linearGradient id="gSent" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gRecv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="label" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} minTickGap={24} />
                  <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} width={48} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--popover-foreground)', fontSize: 12 }} />
                  <Area type="monotone" dataKey="sent" name={t('dashboard.sent')} stroke="#0ea5e9" strokeWidth={2} fill="url(#gSent)" />
                  <Area type="monotone" dataKey="received" name={t('dashboard.received')} stroke="#10b981" strokeWidth={2} fill="url(#gRecv)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Plano + consumo */}
        <Card>
          <CardHeader className="flex-row items-center gap-2">
            <Sparkles className="size-4 text-primary" />
            <CardTitle>Plano</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Link to="/billing" className="rounded-lg border bg-primary/5 p-4 transition-colors hover:bg-primary/10">
              <p className="text-xs text-muted-foreground">Plano atual</p>
              {ent.isLoading
                ? <Skeleton className="mt-1 h-7 w-24" />
                : <p className="mt-1 text-2xl font-bold">{planLabel}{plan === 'pro' && ent.data ? <span className="ml-2 text-sm font-normal text-muted-foreground">{formatMoney(ent.data.plan_monthly_cents, ent.data.currency)}/mês</span> : null}</p>}
            </Link>
            {/* Franquia do Free (some no Pro/cortesia): interações do MÊS, enviadas + recebidas. */}
            <FreeQuotaGauge />
            <div className="rounded-lg border bg-muted/30 p-4">
              <p className="text-xs text-muted-foreground">{t('dashboard.consumption')}</p>
              <p className="mt-1 text-2xl font-bold">{(u.total ?? 0).toLocaleString(locale)}</p>
              <p className="text-xs text-muted-foreground">{t('dashboard.messagesInPeriod', { n: days })}</p>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Smartphone className="size-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t('dashboard.connectedNumbers')}</span>
              <span className="ml-auto font-semibold">{connected}<span className="font-normal text-muted-foreground">/{insts.length}</span></span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ranking de números + por tipo */}
      <div className="grid gap-6 lg:grid-cols-3 *:min-w-0">
        <Card className="lg:col-span-2">
          <CardHeader className="flex-row items-center gap-2">
            <Trophy className="size-4 text-amber-500" />
            <CardTitle>{t('dashboard.topNumbers')}</CardTitle>
          </CardHeader>
          <CardContent>
            {usage.isLoading ? (
              <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : ranking.length === 0 ? (
              insts.length === 0
                ? <Link to="/numbers" className="text-sm text-primary hover:underline">{t('dashboard.addFirst')}</Link>
                : <p className="py-10 text-center text-sm text-muted-foreground">{t('dashboard.noUsage')}</p>
            ) : (
              <ol className="flex flex-col gap-3">
                {ranking.map((n, idx) => {
                  const inst = statusOf[n.instance_id];
                  return (
                    <li key={n.instance_id} className="flex items-center gap-3">
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">{idx + 1}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium">{inst?.nickname || formatPhone(n.phone)}</span>
                          <span className="shrink-0 text-sm font-semibold tabular-nums">{n.total.toLocaleString(locale)}</span>
                        </div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                          <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(4, (n.total / maxRank) * 100)}%` }} />
                        </div>
                      </div>
                      {inst && (
                        <Badge variant={inst.status === 'connected' || inst.status === 'warming' ? 'success' : inst.status === 'connecting' ? 'warning' : 'secondary'} className="shrink-0">
                          {statusLabel(inst.status)}
                        </Badge>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{t('dashboard.byType')}</CardTitle></CardHeader>
          <CardContent>
            {usage.isLoading ? (
              <Skeleton className="mx-auto size-45 rounded-full" />
            ) : byType.length === 0 ? (
              <p className="py-16 text-center text-sm text-muted-foreground">{t('dashboard.noUsage')}</p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={byType} dataKey="n" nameKey="type" cx="50%" cy="50%" innerRadius={48} outerRadius={72} paddingAngle={2} stroke="var(--card)">
                      {byType.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--popover-foreground)', fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <ul className="mt-3 flex flex-col gap-1.5">
                  {byType.slice(0, 6).map((d, i) => (
                    <li key={d.type} className="flex items-center gap-2 text-sm">
                      <span className="size-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="flex-1 truncate capitalize">{d.type}</span>
                      <span className="font-medium tabular-nums">{d.n.toLocaleString(locale)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

